import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Ruler, Sparkles, FileDown, Factory, Home, Layers, Utensils, Package, Loader2, CheckCircle2, Clock, ShieldCheck } from "lucide-react";
import { useAuth, formatApiError } from "@/lib/auth";
import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const FEATURES = [
  { icon: Ruler, title: "Cotes libres", desc: "Ajustez la largeur, la hauteur et la profondeur au millimètre près." },
  { icon: Sparkles, title: "Finitions premium", desc: "Mélaminé, chêne, noyer fumé, laqué mat — le prix se met à jour." },
  { icon: FileDown, title: "Devis PDF instantané", desc: "Générez un devis professionnel signé de l'atelier en un clic." },
  { icon: Factory, title: "Fait en atelier", desc: "Chaque meuble est produit à la commande dans notre atelier français." },
];

const UNIVERSES = [
  { id: "dressing", label: "Dressing & Caissons", desc: "Modulable au mm, drag & drop libre.", icon: Layers },
  { id: "salle_de_bain", label: "Salle de bain", desc: "Grille tarifaire PRO 2026, profondeur 470 mm.", icon: Home },
  { id: "cuisine", label: "Cuisine", desc: "Caissons cuisine, profondeur 560 mm.", icon: Utensils },
  { id: "autres", label: "Autres structures", desc: "Bibliothèques, rangements de bureau.", icon: Package },
];

// Carrousel photo — projets réalisés par le réseau d'artisans (photos réelles de chantiers)
const CAROUSEL_IMAGES = [
  { url: "https://customer-assets-rejwkqb3.emergentagent.net/job_11ee28bd-2827-458a-9678-fedef5e7edf7/artifacts/89d22297919fa162_Sans%20titre%20-%2006%20octobre%202026%20a%CC%80%2010%2C47%2C27%20%288%29-Picsart-AiImageEnhancer.webp", label: "Dressing sur-mesure" },
  { url: "/images/carousel-salle-de-bain.png", label: "Meuble de salle de bain" },
  { url: "https://customer-assets-rejwkqb3.emergentagent.net/job_11ee28bd-2827-458a-9678-fedef5e7edf7/artifacts/58ac01a0fe88175e_1440.webp", label: "Îlot cuisine sur-mesure" },
  { url: "https://customer-assets-rejwkqb3.emergentagent.net/job_11ee28bd-2827-458a-9678-fedef5e7edf7/artifacts/6519a0c72a2df743_IMG_0938.webp", label: "Bibliothèque sur-mesure" },
  { url: "/images/carousel-rangement-bureau.png", label: "Rangement bureau" },
  { url: "/images/carousel-caisson-sdb.webp", label: "Caisson salle de bain" },
];

export default function Landing() {
  const { user, loading } = useAuth();
  const [carouselIdx, setCarouselIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setCarouselIdx((i) => (i + 1) % CAROUSEL_IMAGES.length), 3500);
    return () => clearInterval(t);
  }, []);
  // Gate Option A : tant que l'utilisateur n'est pas connecté, on affiche uniquement
  // l'invitation à s'inscrire (aucun accès aux univers ni au carousel).
  if (loading) {
    return (
      <main data-testid="landing-loading" className="min-h-[80vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </main>
    );
  }
  if (!user) {
    return <LandingGate />;
  }
  const secondaryTo = "/devis";
  const secondaryLabel = "Voir mes devis";
  return (
    <main data-testid="landing-page" className="min-h-screen">
      {/* Hero avec choix d'univers direct + carrousel */}
      <section className="relative overflow-hidden">
        <div className="w-full px-4 sm:px-6 lg:px-10 2xl:px-16 pt-12 lg:pt-20 pb-14 grid lg:grid-cols-[1.05fr_1fr] gap-8 xl:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <p className="eyebrow mb-5">Configurateur B2B · Menuiserie française</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Configurez votre meuble{" "}
              <span className="italic text-accent">sur-mesure.</span>
            </h1>
            <p className="mt-5 max-w-lg text-base text-muted-foreground leading-relaxed">
              Un configurateur pensé pour les professionnels de l&apos;aménagement.
              Choisissez votre univers, ajustez les cotes, obtenez un devis PDF instantané.
            </p>

            {/* 4 tuiles univers cliquables directement */}
            <div className="mt-8 grid grid-cols-2 gap-3">
              {UNIVERSES.map((u) => {
                const Icon = u.icon;
                return (
                  <Link
                    key={u.id}
                    to={`/configurateur?u=${u.id}`}
                    data-testid={`hero-universe-${u.id}`}
                    className="group flex items-start gap-3 p-3.5 rounded-xl border border-border bg-card hover:border-primary hover:shadow-md transition-all"
                  >
                    <div className="w-10 h-10 shrink-0 rounded-lg bg-primary/10 text-primary flex items-center justify-center group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                      <Icon className="w-5 h-5" strokeWidth={1.6} />
                    </div>
                    <div className="min-w-0">
                      <p className="font-heading text-sm leading-tight">{u.label}</p>
                      <p className="text-[11px] text-muted-foreground leading-snug mt-0.5">{u.desc}</p>
                    </div>
                  </Link>
                );
              })}
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <Link to={secondaryTo} data-testid="hero-cta-quotes" className="text-sm font-heading text-primary hover:text-accent inline-flex items-center gap-1.5">
                {secondaryLabel} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>

          {/* Carrousel photo — diaporama des projets */}
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="relative"
            data-testid="hero-carousel"
          >
            <div className="relative aspect-[4/5] rounded-3xl overflow-hidden paper bg-muted">
              {CAROUSEL_IMAGES.map((img, i) => (
                <img
                  key={img.url}
                  src={img.url}
                  alt={img.label}
                  loading={i === 0 ? "eager" : "lazy"}
                  className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${i === carouselIdx ? "opacity-100" : "opacity-0"}`}
                  draggable={false}
                />
              ))}
              <div className="absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-black/70 to-transparent">
                <p className="font-heading text-lg text-white leading-tight">{CAROUSEL_IMAGES[carouselIdx].label}</p>
                <p className="text-[11px] text-white/80 uppercase tracking-[0.2em] mt-1">Projet réalisé sur-mesure</p>
              </div>
              {/* Dots */}
              <div className="absolute bottom-3 right-4 flex gap-1.5">
                {CAROUSEL_IMAGES.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    aria-label={`Voir image ${i + 1}`}
                    onClick={() => setCarouselIdx(i)}
                    className={`w-1.5 h-1.5 rounded-full transition-all ${i === carouselIdx ? "w-6 bg-white" : "bg-white/50 hover:bg-white/80"}`}
                  />
                ))}
              </div>
            </div>
            <div className="absolute -bottom-6 -left-6 paper p-4 shadow-md w-52 hidden sm:block">
              <p className="eyebrow text-[10px]">Devis pro instantané</p>
              <p className="font-heading text-lg mt-1 leading-tight">Prix ferme selon<br/>vos cotes exactes</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Feature strip */}
      <section className="border-y border-border bg-card/60">
        <div className="w-full px-4 sm:px-6 lg:px-10 2xl:px-16 py-14 grid grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-8">
          {FEATURES.map((f) => (
            <div key={f.title} className="flex flex-col gap-3">
              <div className="w-11 h-11 rounded-full bg-background border border-border flex items-center justify-center">
                <f.icon className="w-4 h-4 text-primary" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-xl leading-tight">{f.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Process */}
      <section className="w-full px-4 sm:px-6 lg:px-10 2xl:px-16 py-16 lg:py-24">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-16 items-center">
          <div>
            <p className="eyebrow mb-4">Le process</p>
            <h2 className="text-3xl sm:text-4xl leading-tight">
              Un devis pro,<br />
              <span className="italic text-accent">en trois gestes.</span>
            </h2>
            <p className="text-muted-foreground mt-5 max-w-md">
              Fini les allers-retours d&apos;e-mails avec des tarifs approximatifs.
              Vos artisans revendeurs et cuisinistes gagnent un temps précieux.
            </p>
          </div>
          <ol className="space-y-6">
            {[
              { n: "01", t: "Sélectionnez une catégorie", d: "Choisissez votre univers : Dressing, Salle de Bain, Cuisine ou autres structures." },
              { n: "02", t: "Personnalisez", d: "Ajustez les dimensions au mm, configurez les sections et gérez les options de pose en direct." },
              { n: "03", t: "Générez le devis", d: "Un devis HT en marque blanche avec visuel 2D, prêt à être présenté à votre client ou converti en commande." },
            ].map((s) => (
              <li key={s.n} className="paper p-6 flex gap-5 items-start">
                <span className="font-heading text-3xl text-accent leading-none">{s.n}</span>
                <div>
                  <h3 className="font-heading text-xl">{s.t}</h3>
                  <p className="text-sm text-muted-foreground mt-1">{s.d}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Final CTA */}
      <section className="w-full px-4 sm:px-6 lg:px-10 2xl:px-16 pb-16 lg:pb-24">
        <div className="paper p-12 lg:p-16 text-center relative overflow-hidden">
          <div className="absolute inset-0 wood-grain opacity-50 pointer-events-none" />
          <div className="relative">
            <p className="eyebrow mb-4">Prêt à démarrer ?</p>
            <h2 className="text-3xl sm:text-4xl leading-tight max-w-2xl mx-auto">
              Configurez votre premier caisson — <span className="italic text-accent">sans engagement.</span>
            </h2>
            <Link to="/configurateur" data-testid="final-cta" className="btn-primary mt-8">
              Ouvrir le configurateur <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-border py-8">
        <div className="w-full px-4 sm:px-6 lg:px-10 2xl:px-16 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-muted-foreground">
          <p>© {new Date().getFullYear()} Atelier Inter-Habitat — Menuiserie française</p>
          <p>Prix HT — TVA non applicable, art. 293 B du CGI</p>
        </div>
      </footer>
    </main>
  );
}

function HeroCabinetVisual() {
  const woodDark = "#8B6642";
  const woodMid = "#B08B60";
  const woodLight = "#D4B48A";
  const doorFront = "#E8DFCF";
  const shadow = "rgba(30,20,10,0.35)";
  return (
    <svg
      data-testid="hero-image"
      viewBox="0 0 400 500"
      className="w-full h-full"
      preserveAspectRatio="xMidYMid slice"
      role="img"
      aria-label="Dressing sur-mesure moderne — atelier menuiserie"
    >
      <defs>
        <linearGradient id="floorGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#e6ddc9" />
          <stop offset="1" stopColor="#c9bda2" />
        </linearGradient>
        <linearGradient id="wallGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#f5f0e6" />
          <stop offset="1" stopColor="#e2d8c4" />
        </linearGradient>
        <pattern id="grainH" width="80" height="6" patternUnits="userSpaceOnUse">
          <path d="M0 3 Q 20 1 40 3 T 80 3" stroke="rgba(80,50,20,0.14)" fill="none" strokeWidth="0.8" />
        </pattern>
      </defs>

      {/* Fond mural et sol */}
      <rect x="0" y="0" width="400" height="360" fill="url(#wallGrad)" />
      <rect x="0" y="360" width="400" height="140" fill="url(#floorGrad)" />
      <line x1="0" y1="360" x2="400" y2="360" stroke="rgba(0,0,0,0.15)" strokeWidth="1" />

      {/* Ombre portée */}
      <ellipse cx="200" cy="430" rx="150" ry="10" fill={shadow} opacity="0.4" />

      {/* Caisson principal 3 sections — perspective légère */}
      {/* Corps caisson */}
      <rect x="60" y="80" width="280" height="340" fill={woodMid} rx="3" />
      <rect x="60" y="80" width="280" height="340" fill="url(#grainH)" opacity="0.5" rx="3" />

      {/* Épaisseur haut (perspective) */}
      <polygon points="60,80 340,80 355,60 75,60" fill={woodLight} opacity="0.8" />
      {/* Côté droit */}
      <polygon points="340,80 355,60 355,410 340,420" fill={woodDark} opacity="0.75" />

      {/* Section A (gauche) — portes fermées */}
      <rect x="72" y="92" width="82" height="316" fill={doorFront} rx="2" stroke={woodDark} strokeWidth="1.5" />
      <rect x="77" y="97" width="72" height="306" fill="none" stroke="rgba(0,0,0,0.15)" strokeWidth="0.7" rx="1" />
      {/* Profil Line poignée sur haut de porte */}
      <rect x="80" y="103" width="66" height="3" rx="1.5" fill={woodDark} />

      {/* Section B (milieu) — 3 tiroirs en bas + tablette + porte ouverte simulée */}
      <rect x="159" y="92" width="82" height="316" fill={doorFront} opacity="0.4" rx="2" stroke={woodDark} strokeWidth="1.5" />
      {/* Intérieur visible : tablettes + barre penderie */}
      <line x1="165" y1="140" x2="235" y2="140" stroke={woodDark} strokeWidth="3" strokeLinecap="round" />
      <line x1="165" y1="180" x2="235" y2="180" stroke={woodDark} strokeWidth="2.5" strokeDasharray="4 3" />
      {/* Barre de penderie + cintre */}
      <line x1="170" y1="220" x2="230" y2="220" stroke={woodDark} strokeWidth="4" strokeLinecap="round" />
      <g transform="translate(200 226)">
        <path d="M-9 3 L9 3 L7 8 L-7 8 Z" stroke={woodDark} strokeWidth="1.5" fill="none" strokeLinejoin="round" />
        <path d="M-6 3 Q0 -2 6 3" stroke={woodDark} strokeWidth="1.5" fill="none" strokeLinecap="round" />
        <line x1="0" y1="-1" x2="0" y2="3" stroke={woodDark} strokeWidth="1.5" strokeLinecap="round" />
      </g>
      {/* Bloc tiroirs en bas */}
      {[0, 1, 2].map((i) => (
        <g key={`d${i}`}>
          <rect x="163" y={295 + i * 36} width="74" height="34" fill={woodLight} stroke={woodDark} strokeWidth="1.5" rx="2" />
          <rect x="188" y={310 + i * 36} width="24" height="3" fill={woodDark} rx="1" />
        </g>
      ))}

      {/* Section C (droite) — porte fermée sombre */}
      <rect x="246" y="92" width="82" height="316" fill={doorFront} rx="2" stroke={woodDark} strokeWidth="1.5" />
      <rect x="251" y="97" width="72" height="306" fill="none" stroke="rgba(0,0,0,0.15)" strokeWidth="0.7" rx="1" />
      {/* Étrier Square poignée */}
      <rect x="316" y="240" width="3" height="30" fill={woodDark} rx="1" />
      <rect x="315" y="239" width="5" height="3" fill={woodDark} />
      <rect x="315" y="269" width="5" height="3" fill={woodDark} />

      {/* Séparations verticales */}
      <line x1="156" y1="90" x2="156" y2="410" stroke={woodDark} strokeWidth="2.5" opacity="0.7" />
      <line x1="243" y1="90" x2="243" y2="410" stroke={woodDark} strokeWidth="2.5" opacity="0.7" />

      {/* Pieds */}
      {[72, 152, 242, 328].map((x, i) => (
        <rect key={`f${i}`} x={x} y="410" width="10" height="10" fill={woodDark} rx="1" />
      ))}

      {/* Labels sections */}
      {["A", "B", "C"].map((L, i) => (
        <g key={L} transform={`translate(${113 + i * 87}, 445)`}>
          <circle cx="0" cy="0" r="11" fill="#0465C1" />
          <text x="0" y="4" textAnchor="middle" fontFamily="Outfit, sans-serif" fontSize="12" fontWeight="700" fill="#FFF">{L}</text>
        </g>
      ))}

      {/* Petite info top-left */}
      <g transform="translate(20 30)">
        <rect x="0" y="0" width="120" height="28" fill="rgba(255,255,255,0.9)" rx="14" />
        <circle cx="14" cy="14" r="4.5" fill="#0465C1" />
        <text x="24" y="18" fontFamily="Outfit, sans-serif" fontSize="10" fontWeight="600" fill="#1F1F1F">Dressing sur-mesure</text>
      </g>

      {/* Cotes indicatives */}
      <g stroke="#1F1F1F" strokeWidth="0.7" fontFamily="Outfit, sans-serif" fontSize="9" fill="#1F1F1F" opacity="0.55">
        <line x1="60" y1="70" x2="340" y2="70" strokeDasharray="2 2" />
        <text x="200" y="65" textAnchor="middle">2700 mm</text>
        <line x1="45" y1="80" x2="45" y2="420" strokeDasharray="2 2" />
        <text x="35" y="252" textAnchor="middle" transform="rotate(-90 35 252)">2700 mm</text>
      </g>
    </svg>
  );
}

/* ---------------------------------------------------------------------------
 * LandingGate — écran gate total pour visiteurs non-connectés (Option A stricte).
 * Affiche : pitch marketing (gauche) + formulaire d'inscription 6 champs (droite)
 *           + lien "Se connecter" pour les artisans déjà inscrits.
 * Après soumission, écran confirmation "Compte en attente de validation".
 * ------------------------------------------------------------------------- */
function LandingGate() {
  const { register } = useAuth();
  const [form, setForm] = useState({
    email: "", password: "", contact_name: "", company: "", phone: "", siret: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(form);
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <main data-testid="landing-gate-success" className="min-h-[85vh] flex items-center justify-center px-6 py-10">
        <div className="paper p-10 max-w-md text-center space-y-4">
          <Clock className="w-12 h-12 mx-auto text-primary" strokeWidth={1.4} />
          <h1 className="font-heading text-3xl">Demande enregistrée</h1>
          <p className="text-muted-foreground text-sm leading-relaxed">
            Merci <b>{form.contact_name}</b> ! Votre compte pour <b>{form.company || form.email}</b> a bien été créé et est en attente de validation par notre équipe.
            Vous recevrez un email dès qu&apos;il sera activé.
          </p>
          <div className="text-xs text-muted-foreground bg-secondary/50 rounded-lg p-4 text-left space-y-1">
            <p><b>Email</b> : {form.email}</p>
            {form.company && <p><b>Société</b> : {form.company}</p>}
            {form.siret && <p><b>SIRET</b> : {form.siret}</p>}
          </div>
          <Link to="/login" data-testid="gate-success-login" className="btn-secondary w-full inline-flex items-center justify-center gap-2">
            <ArrowRight className="w-4 h-4" /> Aller à la connexion
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="landing-gate" className="min-h-[85vh]">
      <section className="w-full px-4 sm:px-6 lg:px-10 2xl:px-16 pt-10 lg:pt-14 pb-14 grid lg:grid-cols-[minmax(0,1fr)_minmax(380px,460px)] gap-8 lg:gap-14 items-start">
        {/* LEFT — Pitch marketing */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <p className="eyebrow mb-5">Configurateur B2B · Menuiserie française</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            Un espace pro,{" "}
            <span className="italic text-accent">sur invitation.</span>
          </h1>
          <p className="mt-5 max-w-lg text-base text-muted-foreground leading-relaxed">
            L&apos;accès au configurateur B2B est réservé aux artisans, menuisiers et professionnels de l&apos;aménagement.
            Créez votre compte pour découvrir les 4 univers, configurer vos meubles au millimètre et générer vos devis en marque blanche.
          </p>

          <ul className="mt-8 space-y-3 text-sm">
            <li className="flex gap-3 items-start">
              <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
              <span><b>4 univers dédiés</b> : Dressing, Salle de bain, Cuisine, Autres structures.</span>
            </li>
            <li className="flex gap-3 items-start">
              <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
              <span><b>Devis PDF marque blanche</b> à votre nom d&apos;entreprise, sans mention de fournisseur.</span>
            </li>
            <li className="flex gap-3 items-start">
              <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
              <span><b>Tarification transparente</b> mise à jour en temps réel — nomenclature PRO 2026.</span>
            </li>
            <li className="flex gap-3 items-start">
              <ShieldCheck className="w-5 h-5 text-primary shrink-0 mt-0.5" />
              <span><b>Validation manuelle</b> de chaque inscription pour garantir un réseau 100% professionnel.</span>
            </li>
          </ul>

          <div className="mt-10 text-sm text-muted-foreground">
            Déjà un compte ?{" "}
            <Link to="/login" data-testid="gate-login-link" className="text-primary font-medium hover:underline inline-flex items-center gap-1">
              Se connecter <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </motion.div>

        {/* RIGHT — Formulaire inscription 6 champs */}
        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          data-testid="gate-register-form"
          className="paper p-6 lg:p-8 space-y-4 w-full"
        >
          <div>
            <p className="eyebrow mb-2">Créer un compte pro</p>
            <h2 className="font-heading text-2xl">Rejoignez le réseau</h2>
          </div>

          <div>
            <Label>Nom complet *</Label>
            <Input data-testid="gate-name" required value={form.contact_name} onChange={set("contact_name")} placeholder="Prénom et nom" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Société *</Label>
              <Input data-testid="gate-company" required value={form.company} onChange={set("company")} placeholder="Menuiserie Dupont" />
            </div>
            <div>
              <Label>SIRET *</Label>
              <Input
                data-testid="gate-siret"
                required
                value={form.siret}
                onChange={set("siret")}
                inputMode="numeric"
                pattern="[0-9 ]*"
                minLength={14}
                maxLength={17}
                placeholder="14 chiffres"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Email *</Label>
              <Input data-testid="gate-email" type="email" required value={form.email} onChange={set("email")} placeholder="vous@entreprise.fr" />
            </div>
            <div>
              <Label>Téléphone *</Label>
              <Input data-testid="gate-phone" required value={form.phone} onChange={set("phone")} placeholder="06 12 34 56 78" />
            </div>
          </div>

          <div>
            <Label>Mot de passe * <span className="text-muted-foreground text-xs font-normal">(6 caractères min.)</span></Label>
            <Input data-testid="gate-password" type="password" required minLength={6} value={form.password} onChange={set("password")} />
          </div>

          <Button data-testid="gate-submit" type="submit" className="btn-primary w-full" disabled={loading}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Créer mon compte <ArrowRight className="w-4 h-4" /></>}
          </Button>

          <p className="text-xs text-muted-foreground text-center leading-relaxed">
            Votre inscription sera validée par notre équipe sous 24h ouvrées.
            <br />
            Déjà inscrit ?{" "}
            <Link to="/login" data-testid="gate-form-login-link" className="text-primary font-medium hover:underline">
              Se connecter
            </Link>
          </p>
        </motion.form>
      </section>
    </main>
  );
}

